import React from 'react';
import { H2, P, OL, LI, FeatureLink, Callout } from './_shared';

export default {
  slug: 'getting-started',
  title: 'Getting started in 5 minutes',
  category: 'getting-started',
  summary: 'A quick tour of the first things to set up after signing up — workspace, contacts, and your first deal.',
  searchTerms: ['onboarding', 'first time', 'new user', 'setup', 'tour'],
  related: ['adding-clients', 'creating-deals', 'managing-pipeline'],
  Body: () => (
    <>
      <P>
        RealtyFlows is built around three core objects: <strong>clients</strong>{' '}
        (your leads and past customers), <strong>deals</strong> (a specific
        transaction), and <strong>folders</strong> (everything that has to be
        signed, stored, or filed for a deal). Get a handle on those and the
        rest of the product will feel familiar.
      </P>

      <H2>The 5-minute setup</H2>
      <OL>
        <LI>
          <strong>Set your profile.</strong> Go to{' '}
          <FeatureLink to="/settings">Settings</FeatureLink> and fill in your
          name, brokerage, license number, and signature image. The signature
          and license show up on every doc you send out.
        </LI>
        <LI>
          <strong>Add a few clients.</strong> Hit{' '}
          <FeatureLink to="/clients">Clients → New Client</FeatureLink> for the
          three or four people you're currently working with. You can bulk
          import a CSV later — this just gives you something to work with.
        </LI>
        <LI>
          <strong>Open your pipeline.</strong>{' '}
          <FeatureLink to="/deals">Deals</FeatureLink> shows a Kanban board of
          where every transaction stands. Drag a card to move it through
          stages.
        </LI>
        <LI>
          <strong>Create a folder.</strong> When a client is ready to make an
          offer, open their detail page and click "New Folder." That's the
          container for the purchase agreement and every other doc.
        </LI>
      </OL>

      <Callout kind="tip">
        Everything you do in the app shows up in a per-client activity feed.
        That feed is also what feeds the AI suggestions and the broker
        dashboard, so the more you log, the smarter it gets.
      </Callout>

      <H2>What to read next</H2>
      <P>
        If you're going to spend your first week somewhere, spend it learning{' '}
        <FeatureLink to="/help/folders-intro">how folders work</FeatureLink>.
        They're the single most differentiated part of RealtyFlows and they
        save the most time once you have the muscle memory.
      </P>
    </>
  ),
};
